import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useFetchAllSchemeMutation } from "../../../features/tenant/scheme/schemeApi";
import {
  useFetchSchemeMembershipSummaryMutation,
  useFetchSchemeMembershipUserLedgerMutation,
} from "../../../features/tenant/schemeMembership/schemeMembershipApi";

/**
 * Scheme Membership — Tracking.
 *
 * Two views over the same data: a per-period roll-up for a scheme, and the
 * audit ledger for one user.
 *
 * The number to watch is "Pending" on a period whose end date has passed. The
 * settlement sweep enqueues one payout per user per closed period, so a period
 * that stays pending means the sweep is behind or stuck — which is the one
 * failure mode that silently leaves users unpaid.
 */

const inputClass =
  "block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";

const EVENT_STYLES = {
  settlement: "bg-green-100 text-green-700",
  reward: "bg-blue-100 text-blue-700",
  clawback: "bg-red-100 text-red-700",
  tier_change: "bg-gray-100 text-gray-600",
  void: "bg-amber-100 text-amber-700",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "—";
  }
};

const num = (v) => Number(v ?? 0).toLocaleString();

const MembershipTracking = () => {
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData")) || {};
  const token = sessionStorage.getItem("access_token");

  const [schemeList, setSchemeList] = useState([]);
  const [schemeId, setSchemeId] = useState("");
  const [summary, setSummary] = useState(null);

  const [appUserId, setAppUserId] = useState("");
  const [ledger, setLedger] = useState(null);

  const [fetchAllScheme, { data: schemeData }] = useFetchAllSchemeMutation();
  const [fetchSummary, { isLoading: loadingSummary }] =
    useFetchSchemeMembershipSummaryMutation();
  const [fetchLedger, { isLoading: loadingLedger }] =
    useFetchSchemeMembershipUserLedgerMutation();

  useEffect(() => {
    fetchAllScheme({
      token,
      tenant_id: vendorData.tenant_id,
      body: { limit: 500, offset: 0, status: "1" },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // /tenant/scheme returns { body: { data: [...], count } } — not a bare array.
    const rows = schemeData?.body?.data;
    setSchemeList(Array.isArray(rows) ? rows : []);
  }, [schemeData]);

  const primarySchemes = useMemo(
    () =>
      (Array.isArray(schemeList) ? schemeList : []).filter(
        (s) => s.is_primary === true || s.is_primary === "true",
      ),
    [schemeList],
  );

  const loadSummary = async (id) => {
    if (!id) return;
    try {
      const res = await fetchSummary({
        token,
        tenant_id: vendorData.tenant_id,
        scheme_id: id,
      }).unwrap();
      setSummary(res?.body ?? null);
    } catch (err) {
      toast.error(err?.data?.message || "Could not load the summary", { id: "summaryFailed" });
    }
  };

  const loadLedger = async () => {
    if (!appUserId) {
      toast.error("Enter an app user id", { id: "noUser" });
      return;
    }
    try {
      const res = await fetchLedger({
        token,
        tenant_id: vendorData.tenant_id,
        app_user_id: appUserId,
        scheme_id: schemeId || undefined,
        limit: 100,
      }).unwrap();
      setLedger(res?.body?.ledger ?? []);
    } catch (err) {
      toast.error(err?.data?.message || "Could not load the ledger", { id: "ledgerFailed" });
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="w-full px-6 pt-4 pb-3">
          <h1 className="text-xl font-semibold text-gray-900">Scheme Membership Tracking</h1>
          <LinksComponent />
          <p className="text-sm text-gray-500 mt-1">
            Per-period payouts for a scheme, and the full audit trail for a single user.
          </p>
        </div>
      </div>

      <div className="w-full px-6 py-5 space-y-5 flex-1">

        {/* Per-scheme roll-up */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex flex-col md:flex-row md:items-end gap-4 mb-5">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Scheme</label>
              <select
                className={inputClass}
                value={schemeId}
                onChange={(e) => {
                  setSchemeId(e.target.value);
                  setSummary(null);
                  loadSummary(e.target.value);
                }}
              >
                <option value="">Select a primary scheme</option>
                {primarySchemes.map((s) => (
                  <option key={s.id} value={s.id}>
                    #{s.id} — {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingSummary && <p className="text-sm text-gray-500">Loading…</p>}

          {summary && summary.periods?.length === 0 && (
            <p className="text-sm text-gray-500">
              Nobody has accrued against this scheme yet.
            </p>
          )}

          {summary && summary.periods?.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Period</th>
                    <th className="text-left px-3 py-2">Window</th>
                    <th className="text-right px-3 py-2">Users</th>
                    <th className="text-right px-3 py-2">Settled</th>
                    <th className="text-right px-3 py-2">Pending</th>
                    <th className="text-right px-3 py-2">Points Scanned</th>
                    <th className="text-right px-3 py-2">Paid Out</th>
                    <th className="text-left px-3 py-2">Tiers</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.periods.map((p) => {
                    const closed = p.period_end && new Date(p.period_end) <= new Date();
                    const stuck = closed && p.pending_settlement > 0;
                    return (
                      <tr key={String(p.period_index)} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {p.period_index === null ? "Lifetime" : `P${p.period_index}`}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {p.period_start ? `${fmtDate(p.period_start)} → ${fmtDate(p.period_end)}` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900">{num(p.unique_users)}</td>
                        <td className="px-3 py-2 text-right text-gray-900">{num(p.settled_users)}</td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className={
                              stuck
                                ? "inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"
                                : "text-gray-500"
                            }
                            title={
                              stuck
                                ? "This period has closed but not everyone has been settled — check the settlement worker"
                                : ""
                            }
                          >
                            {num(p.pending_settlement)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{num(p.total_progress)}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900">
                          {num(p.total_paid)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          <div className="flex flex-wrap gap-1">
                            {(p.tiers || [])
                              .filter((t) => t.tier_name)
                              .map((t) => (
                                <span
                                  key={`${t.tier_rank}-${t.tier_name}`}
                                  className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-xs"
                                >
                                  {t.tier_name}: {t.users}
                                </span>
                              ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Per-user ledger */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">User Audit Trail</h2>
          <p className="text-sm text-gray-500 mb-5">
            Material events only — a tier change, a payout, a clawback, a settlement or a void.
            Ordinary scans are not listed.
          </p>

          <div className="flex flex-col md:flex-row md:items-end gap-3 mb-5">
            <div className="flex-1 md:max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">App User ID</label>
              <input
                className={inputClass}
                value={appUserId}
                placeholder="e.g. 8073"
                onChange={(e) => setAppUserId(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={loadLedger}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Load
            </button>
          </div>

          {loadingLedger && <p className="text-sm text-gray-500">Loading…</p>}

          {ledger && ledger.length === 0 && (
            <p className="text-sm text-gray-500">No membership events for this user.</p>
          )}

          {ledger && ledger.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Event</th>
                    <th className="text-left px-3 py-2">Period</th>
                    <th className="text-left px-3 py-2">Tier</th>
                    <th className="text-right px-3 py-2">Progress</th>
                    <th className="text-right px-3 py-2">Reward</th>
                    <th className="text-right px-3 py-2">Moved</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-600">{fmtDateTime(row.created_at)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            EVENT_STYLES[row.event] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {row.event}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {row.period_index === null ? "Lifetime" : `P${row.period_index}`}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {row.prev_tier_name && row.prev_tier_name !== row.tier_name
                          ? `${row.prev_tier_name} → ${row.tier_name}`
                          : row.tier_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">{num(row.progress_value)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {row.reward_type === "percentage" && row.reward_percent
                          ? `${row.reward_percent}% of ${num(row.reward_base)} = ${num(row.reward_amount)}`
                          : num(row.reward_amount)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-medium ${
                          Number(row.delta) < 0 ? "text-red-600" : "text-gray-900"
                        }`}
                      >
                        {Number(row.delta) > 0 ? "+" : ""}
                        {num(row.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipTracking;
